/**
 * Human label for `metadata.scene_id`. Two vocabularies mint that field: the
 * builder's SEQUENCER plan emits `step-00N` (assets/system/builder-system-prompt.txt),
 * classic DISTRIBUTOR templates emit `scene-00N` (assets/prompts/*-distributor-prompt.json).
 * The engine treats the value as opaque — nothing branches on the prefix, it
 * only has to match between a run's fan-out and its connector/assembly — but
 * the storyboard used to prettify only the `scene-` spelling, so a sequencer
 * clip rendered raw as `step-004` next to a distributor clip's `Scene 001` and
 * that divergence was mistaken for two different frontends.
 *
 * Route every scene-id DISPLAY through here instead of a local
 * `.replace('scene-', 'Scene ')`. Never use this on a value that is sent to an
 * API or matched against another id — this is presentation only.
 *
 * Returns title case (`Scene 003`); a caller that wants the uppercase
 * treatment applies `.toUpperCase()` itself, same as before.
 *
 * `index` is only consulted for the `ungrouped-*` fallback (an item with no
 * scene_id at all, numbered by its position); callers with no natural index —
 * this id will never be `ungrouped-*` in their domain — can omit it.
 */
export const formatSceneLabel = (sceneId: string, index: number = 0): string => {
  if (sceneId.startsWith('ungrouped')) {
    return `Scene ${(index + 1).toString().padStart(3, '0')}`;
  }
  if (sceneId.startsWith('scene-')) return sceneId.replace('scene-', 'Scene ');
  if (sceneId.startsWith('step-')) return sceneId.replace('step-', 'Scene ');
  // Unrecognised vocabulary: still show something readable rather than a raw
  // id, e.g. `intro_beat` -> `Intro Beat`.
  const spaced = sceneId.replace(/[-_]+/g, ' ').trim();
  return spaced
    ? spaced.replace(/\b\w/g, (c) => c.toUpperCase())
    : `Scene ${(index + 1).toString().padStart(3, '0')}`;
};
